import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Box, Paper, Stack } from '@mui/material'

import DownloadIcon from '@mui/icons-material/Download'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import errorHandlerHook from '@hooks/useErrorHandler'
import responsiveHook from '@hooks/useResponsive'

import LeadService from '@services/leads'

import { TLGTypography } from '@components/TLGTypography'
import { TextField } from '@components/TextField'

import { scaleUpTransition } from '@utils/styles'
import { LeadSource } from '@utils/types'

import {
  bookImageSx,
  bookImageWrapperSx,
  fieldsWrapperSx,
  formContainerSx,
  inputSx,
  leadMagnetWrapperSx,
  loadingButtonSx,
  submitButtonIconSx,
  textSx,
  textWrapperSx,
} from './styles'

import { RegisteredLeadFeedback } from './RegisteredLeadFeedback'

type FormData = {
  firstName: string
  lastName: string
  companyName: string
  companyEmail: string
}

const MAX_NAME_LENGTH = 64
const MAX_EMAIL_LENGTH = 80

const INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  companyName: '',
  companyEmail: '',
}

const LeadMagnet = () => {
  const { t } = useTranslation()

  const { isMobile, ifDesktop } = responsiveHook.useResponsive()

  const [isLeadRegistered, setIsLeadRegistered] = useState(false)

  const SCHEMA = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('First name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      )
      .required(t('First name is mandatory')),
    lastName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t('Last name must be {{maxLengthName}} characters or less', {
          maxLengthName: MAX_NAME_LENGTH,
        })
      )
      .required(t('Last name is mandatory')),
    companyEmail: Yup.string()
      .trim()
      .email(t('Invalid email'))
      .required(t('Company e-mail is mandatory')),
    companyName: Yup.string()
      .trim()
      .max(
        MAX_NAME_LENGTH,
        t(`Company name must be {{maxLengthName}} characters or less`, {
          maxLengthName: MAX_NAME_LENGTH,
        })
      ),
  })

  const handleOnSubmit = async ({
    firstName,
    lastName,
    companyEmail,
    companyName,
  }: FormData) => {
    try {
      await LeadService.create({
        lead: {
          firstName,
          lastName,
          companyEmail,
          companyName,
        },
        source: LeadSource.IT_CUSTOMER_LEAD_MAGNET,
      })

      setIsLeadRegistered(true)
    } catch (err) {
      handleError({ exception: err })
    }
  }

  const formik = useFormik<FormData>({
    validationSchema: SCHEMA,
    validateOnBlur: false,
    validateOnMount: true,
    validateOnChange: true,
    initialValues: INITIAL_VALUES,
    onSubmit: handleOnSubmit,
  })

  const { handleError } = errorHandlerHook.useErrorHandler(formik)

  const isSubmitButtonDisabled = !formik.isValid || !formik.dirty

  return (
    <Paper
      elevation={3}
      sx={leadMagnetWrapperSx}
      data-testid="lead-magnet-section"
    >
      {isLeadRegistered ? (
        <RegisteredLeadFeedback />
      ) : (
        <Box sx={bookImageWrapperSx}>
          <Box
            alt={t<string>('Tech Vendor Scorecard book illustration')}
            loading="lazy"
            component="img"
            src="/images/landing-pages/lead-magnet/book.webp"
            sx={{
              ...bookImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
          <Box sx={textWrapperSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={textSx}
            >
              {t('Unlock Precision in Tech Vendor Selection')}
            </TLGTypography>
            <TLGTypography
              responsiveVariant={{ xs: 'subtitle1', md: 'h6' }}
              sx={textSx}
            >
              {t(
                'Download our exclusive Tech Vendor Scorecard now and streamline your journey to the perfect technology partner.'
              )}
            </TLGTypography>
            <Stack
              component="form"
              onSubmit={formik.handleSubmit}
              sx={formContainerSx}
            >
              <Box sx={fieldsWrapperSx}>
                <TextField
                  fullWidth
                  aria-label={t('First Name')}
                  label={t('First Name*')}
                  size="small"
                  name="firstName"
                  variant="filled"
                  value={formik.values.firstName}
                  error={
                    !!formik.errors.firstName && !!formik.touched.firstName
                  }
                  helperText={
                    formik.touched.firstName ? formik.errors.firstName : ''
                  }
                  inputProps={{
                    sx: inputSx,
                    'data-testid': 'first-name-field',
                    maxLength: MAX_NAME_LENGTH,
                  }}
                  showMaxLengthInHelperText
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <TextField
                  fullWidth
                  aria-label={t('Last Name')}
                  label={t('Last Name*')}
                  size="small"
                  name="lastName"
                  variant="filled"
                  value={formik.values.lastName}
                  error={!!formik.errors.lastName && !!formik.touched.lastName}
                  helperText={
                    formik.touched.lastName ? formik.errors.lastName : ''
                  }
                  inputProps={{
                    sx: inputSx,
                    'data-testid': 'last-name-field',
                    maxLength: MAX_NAME_LENGTH,
                  }}
                  showMaxLengthInHelperText
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Box>
              <Box sx={fieldsWrapperSx}>
                <TextField
                  fullWidth
                  aria-label={t('Company Email')}
                  label={t('Company Email*')}
                  size="small"
                  name="companyEmail"
                  variant="filled"
                  value={formik.values.companyEmail}
                  error={
                    !!formik.errors.companyEmail &&
                    !!formik.touched.companyEmail
                  }
                  helperText={
                    formik.touched.companyEmail
                      ? formik.errors.companyEmail
                      : ''
                  }
                  inputProps={{
                    sx: inputSx,
                    'data-testid': 'company-email-field',
                    maxLength: MAX_EMAIL_LENGTH,
                  }}
                  showMaxLengthInHelperText
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <TextField
                  fullWidth
                  aria-label={t('Company Name')}
                  label={t('Company Name')}
                  size="small"
                  name="companyName"
                  variant="filled"
                  value={formik.values.companyName}
                  error={
                    !!formik.errors.companyName && !!formik.touched.companyName
                  }
                  helperText={
                    formik.touched.companyName ? formik.errors.companyName : ''
                  }
                  inputProps={{
                    sx: inputSx,
                    'data-testid': 'company-name-field',
                    maxLength: MAX_NAME_LENGTH,
                  }}
                  showMaxLengthInHelperText
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Box>
              <LoadingButton
                type="submit"
                data-testid="get-the-scoreboard-now-button"
                loading={formik.isSubmitting}
                disabled={isSubmitButtonDisabled}
                variant={!isSubmitButtonDisabled ? 'contained' : 'outlined'}
                sx={loadingButtonSx}
                color="secondary"
                size={isMobile ? 'small' : 'large'}
                endIcon={<DownloadIcon sx={submitButtonIconSx} />}
              >
                {!isSubmitButtonDisabled
                  ? t('Get the scoreboard now')
                  : t('Complete the form')}
              </LoadingButton>
            </Stack>
          </Box>
        </Box>
      )}
    </Paper>
  )
}

export { LeadMagnet }
